import React, { useState, useMemo } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

/**
 * MVP Sprint Item 2 — Commitment Department selector.
 * - Accepts a free-text input + up to MAX_CHIPS departments.
 * - Suggestions: caller passes `suggestions` (e.g. case.subject_departments).
 * - Each chip max 32 chars (matching backend cap).
 */
const MAX_CHIPS = 10;
const MAX_CHIP_LENGTH = 32;

export function DepartmentMultiSelect({
  value = [],
  onChange,
  suggestions = [],
  placeholder = 'Type a department and press Enter…',
  testIdPrefix = 'tf-dept-select',
}) {
  const [draft, setDraft] = useState('');

  const remainingSuggestions = useMemo(
    () => (suggestions || []).filter((s) => s && !value.includes(s)).slice(0, 8),
    [suggestions, value],
  );

  const addChip = (raw) => {
    const trimmed = String(raw || '').trim().slice(0, MAX_CHIP_LENGTH);
    if (!trimmed) return;
    if (value.includes(trimmed)) { setDraft(''); return; }
    if (value.length >= MAX_CHIPS) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  const removeChip = (chip) => onChange(value.filter((v) => v !== chip));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChip(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      e.preventDefault();
      removeChip(value[value.length - 1]);
    }
  };

  return (
    <div data-testid={testIdPrefix}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white p-1.5 focus-within:border-blue-400">
        {value.map((chip) => (
          <Badge
            key={chip}
            variant="outline"
            data-testid={`${testIdPrefix}-chip-${chip}`}
            className="bg-blue-50 text-blue-800 border-blue-200 pr-0.5"
          >
            <span className="mr-1">{chip}</span>
            <button
              type="button"
              onClick={() => removeChip(chip)}
              className="inline-flex h-4 w-4 items-center justify-center rounded hover:bg-blue-200"
              aria-label={`Remove ${chip}`}
              data-testid={`${testIdPrefix}-remove-${chip}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_CHIP_LENGTH))}
          onKeyDown={handleKeyDown}
          onBlur={() => draft && addChip(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={value.length >= MAX_CHIPS}
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 min-w-[140px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-1 h-7 text-xs"
        />
      </div>
      {value.length >= MAX_CHIPS && (
        <p className="mt-1 text-xs text-amber-700">Maximum {MAX_CHIPS} departments reached.</p>
      )}
      {remainingSuggestions.length > 0 && value.length < MAX_CHIPS && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <span className="text-xs text-slate-500">Suggestions:</span>
          {remainingSuggestions.map((s) => (
            <Button
              key={s}
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => addChip(s)}
              data-testid={`${testIdPrefix}-suggest-${s}`}
              className="h-5 px-1.5 py-0 text-xs text-slate-600 hover:bg-slate-100"
            >
              <Plus className="h-2.5 w-2.5 mr-0.5" />{s}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default DepartmentMultiSelect;
